import React, { useEffect, useState } from "react";
import {
  Card,
  Radio,
  Input,
  Dropdown,
  Menu,
  Button,
  Typography,
  Row,
  Col,
  Alert,
  Spin,
  message,
  Modal,
  Tooltip,
} from "antd";
import { Link } from "react-router-dom";
import lottieLoader from "assets/moneyLoader.json"; // adjust path as needed
import Player from "lottie-react";
import { useNavigate, useParams } from "react-router-dom";
import { isUuid } from "utility/constant";
import {
  getEpcEmployerPlan,
  listPaymentHistoryByEmployer,
} from "graphql/customEpcQuery";
import { API } from "aws-amplify";
import { CheckOutlined, MailOutlined, MoreOutlined } from "@ant-design/icons";
import { callGraphQLBatch, callGraphQLGet } from "utils/api";
import { updateEpcEmployerPlan } from "graphql/customEpcMutation";
import CommonTable from "modules/PrimaryCare/commonComponents/Table";

const { Text } = Typography;

const LottieSpin = () => (
  <div
    style={{
      position: "absolute",
      zIndex: 10,
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(255,255,255,0.5)",
      backdropFilter: "blur(6px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 16,
    }}
  >
    <Player
      autoplay
      loop
      animationData={lottieLoader}
      style={{ width: 220, height: 220 }}
    />
  </div>
);

export default function EmployerPayments() {
  const { id: employerID } = useParams<{ id: string }>();
  const [plan, setPlan] = useState<"fixed" | "custom" | null>(null);
  const [customValue, setCustomValue] = useState<string>("");
  const [editMode, setEditMode] = useState<boolean>(true);
  const [isFirstTime, setIsFirstTime] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [dpcBillingId, setDpcBillingId] = useState<string | null>(null);
  const [remarkLoading, setRemarkLoading] = useState<boolean>(false);
  const [remarkModal, setRemarkModal] = useState<{
    visible: boolean;
    record: any;
  }>({ visible: false, record: null });
  const [remarkInput, setRemarkInput] = useState("");
  const navigate = useNavigate();

  const [paymentHistory, setPaymentHistory] = useState<
    {
      key: string;
      month: string;
      paymentLink: string;
      amount: string;
      count?: number;
      planPrice?: number;
      status: string;
      remarks: string;
      action: string;
    }[]
  >([]);

  const paymentColumns = [
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      width: 100,
    },
    {
      title: "Plan",
      dataIndex: "planPrice",
      key: "planPrice",
      width: 100,
      render: (planPrice: string) => (
        <span
          style={{
            fontWeight: 600,
            letterSpacing: 0.5,
          }}
        >
          ${planPrice}
        </span>
      ),
    },
    {
      title: "Employee Count",
      dataIndex: "count",
      key: "count",
      width: 100,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 100,
      render: (amount: string) => (
        <span
          style={{
            fontWeight: 600,
            letterSpacing: 0.5,
          }}
        >
          ${amount}
        </span>
      ),
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <span
          style={{
            color: status === "PAID" ? "#52c41a" : "#faad14",
            fontWeight: 600,
            letterSpacing: 0.5,
          }}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 100,
      render: (text: string) =>
        text ? (
          <Tooltip title={text}>
            <span
              style={{
                display: "inline-block",
                maxWidth: 120,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                verticalAlign: "middle",
              }}
            >
              {text}
            </span>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_: any, record: any) =>
        record.status !== "PAID" ? (
          <>
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="resendInvoice"
                    onClick={() => handleResendInvoice(record)}
                  >
                    <MailOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    Resend Invoice
                  </Menu.Item>
                  <Menu.Item
                    key="markAsPaid"
                    onClick={() => handleOpenRemarkModal(record)}
                  >
                    <CheckOutlined
                      style={{ color: "#52c41a", marginRight: 8 }}
                    />
                    Mark as Paid
                  </Menu.Item>
                </Menu>
              }
              overlayClassName="download-btn"
              placement="bottomRight"
            >
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
          </>
        ) : (
          "-"
        ),
    },
  ];

  const handleResendInvoice = async (record: any) => {
    try {
      setLoading(true);
      // Replace with your actual API call for resending invoice
      await API.post("bazarhealthREST", "/epc/superadmin/invoiceResend", {
        body: {
          paymentId: record.key,
        },
      });
      message.success("Invoice resent successfully!");
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          "Failed to resend invoice. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployerPlanandPaymentHistory = async () => {
    setLoading(true);
    if (!employerID || !isUuid.test(employerID)) {
      navigate("/404", {
        state: { message: `No data found for Employer ${employerID}` },
      });
      return;
    }
    try {
      const [employer, [listPaymentHistory]] = await Promise.all([
        callGraphQLGet({
          query: getEpcEmployerPlan,
          variables: { id: employerID },
          outputKeyword: "getEpcEmployer",
        }),
        callGraphQLBatch([
          {
            query: listPaymentHistoryByEmployer,
            authMode: "AMAZON_COGNITO_USER_POOLS",
            variables: {
              employerID,
              sortDirection: "DESC",
            },
            outputKeyword: "listPaymentHistoryByEmployer",
          },
        ]),
      ]);

      if (Array.isArray(listPaymentHistory)) {
        setPaymentHistory(
          listPaymentHistory.map((item, idx) => ({
            key: item.id || idx,
            month: item.billingMonth,
            paymentLink: item.invoiceUrl,
            amount: item.totalAmount,
            count: item.employeeCount,
            planPrice: item.basePrice,
            status: item.invoiceStatus,
            remarks: item.remarks,
            action: item.invoicePdf,
            invoiceId: item.invoiceId,
          }))
        );
      }

      if (!employer) {
        navigate("/404", {
          state: { message: `No data found for Employer ${employerID}` },
        });
      }
      setDpcBillingId(employer.DpcBillingID || null);
      if (
        employer.planPrice &&
        employer.billingStartMonth &&
        employer.isOnlineInvoicingActive
      ) {
        if (employer.planPrice === 100) {
          setPlan("fixed");
          setCustomValue("");
        } else {
          setPlan("custom");
          setCustomValue(String(employer.planPrice));
        }

        setEditMode(false); // Plan already selected, not in edit mode
      } else {
        setPlan(null);
        setCustomValue("");
        setIsFirstTime(true);
        setEditMode(true); // Allow selection
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    if (!plan) {
      setError("Please select a payment plan.");
      return;
    }
    if (plan === "custom") {
      const amount = Number(customValue);
      if (!customValue) {
        setError("Please enter a custom amount.");
        return;
      }
      if (isNaN(amount) || amount < 50 || amount > 200) {
        setError("Custom amount must be between $50 and $200.");
        return;
      }
    }
    setLoading(true);

    try {
      let variables = {
        input: Object.assign(
          {
            id: employerID,
            planPrice: plan === "fixed" ? 100 : Number(customValue),
          },
          isFirstTime && {
            billingStartMonth: new Date().toISOString().slice(0, 10),
            isOnlineInvoicingActive: true,
          }
        ),
      };

      await API.graphql({
        query: updateEpcEmployerPlan,
        variables,
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      setEditMode(false);
      message.success("Payment plan saved successfully!"); // <-- Success toast
    } catch (e) {
      setError("Failed to save payment plan.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRemarkModal = (record: any) => {
    setRemarkModal({ visible: true, record });
    setRemarkInput("");
  };

  const handleRemarkSubmit = async () => {
    // Call your API here with remarkInput and remarkModal.record
    // Example:
    // await apiCallToUpdateRemark(remarkModal.record.key, remarkInput);
    setRemarkLoading(true);
    try {
      const myInit = {
        body: {
          employerId: employerID,
          dpcBillingId: dpcBillingId,
          paymentHistoryId: remarkModal.record.key,
          remark: remarkInput,
          invoiceId: remarkModal.record.invoiceId,
        },
      };

      const response = await API.post(
        "bazarhealthREST",
        "/epc/superadmin/markAsPaid",
        myInit
      );

      message.open({
        type: "success",
        content: response.message || "Transaction marked as paid successfully!",
      });
      fetchEmployerPlanandPaymentHistory();
    } catch (error: any) {
      message.open({
        type: "error",
        content:
          error.response.data.message ||
          "Failed to mark transaction as paid. Please try again.",
      });
    } finally {
      setRemarkLoading(false);
      setRemarkModal({ visible: false, record: null });
      setRemarkInput("");
    }
  };

  const handleEdit = () => {
    setEditMode(true);
    setError(null);
  };

  useEffect(() => {
    // setLoading(true);
    fetchEmployerPlanandPaymentHistory();
  }, [employerID]);

  const customInputStyle: React.CSSProperties = {
    width: 120,
    borderRadius: 8,
    fontSize: 16,
    background: "transparent",
    boxShadow: "none",
    outline: "none",
    border: "1px solid #d9d9d9",
    textAlign: "center",
  };

  const isSelectable = editMode || plan === null;

  return (
    <Spin spinning={loading} indicator={<LottieSpin />}>
      <div
        style={{
          maxWidth: 900,
          margin: "40px auto 0",
          textAlign: "center",
          background: "#fff",
          borderRadius: 16,
          // minHeight: 400,
          position: "relative", // <-- important!
          overflow: "hidden",
        }}
      >
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{
              marginBottom: 16,
              maxWidth: 400,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          />
        )}
        <Row
          gutter={[24, 24]}
          justify="center"
          style={{ marginBottom: 16, flexWrap: "wrap" }}
        >
          <Col xs={24} sm={12} md={10} lg={8}>
            <Card
              style={{
                minWidth: 260,
                borderColor: plan === "fixed" ? "#00274c" : "#e0e0e0",
                borderWidth: 2,
                borderStyle: "solid",
                borderRadius: 16,
                boxShadow: plan === "fixed" ? "0 0 12px #e0e0e0" : undefined,
                transition: "border-color 0.2s, box-shadow 0.2s",
                cursor: isSelectable ? "pointer" : "default",
                opacity: isSelectable ? 1 : 0.6,
              }}
              bodyStyle={{
                padding: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={() => {
                if (isSelectable) {
                  setPlan("fixed");
                  if (plan === null) setEditMode(true);
                  setError(null);
                }
              }}
              hoverable={isSelectable}
            >
              <Radio
                checked={plan === "fixed"}
                disabled={!isSelectable}
                style={{ marginRight: 12 }}
              />
              <Text strong style={{ fontSize: 24 }}>
                $100
              </Text>
              <Text style={{ marginLeft: 4, fontSize: 16 }}>/member</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={10} lg={8}>
            <Card
              style={{
                minWidth: 260,
                borderColor: plan === "custom" ? "#00274c" : "#e0e0e0",
                borderWidth: 2,
                borderStyle: "solid",
                borderRadius: 16,
                boxShadow: plan === "custom" ? "0 0 12px #e0e0e0" : undefined,
                transition: "border-color 0.2s, box-shadow 0.2s",
                cursor: isSelectable ? "pointer" : "default",
                opacity: isSelectable ? 1 : 0.6,
              }}
              bodyStyle={{
                padding: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={() => {
                if (isSelectable) {
                  setPlan("custom");
                  if (plan === null) setEditMode(true);
                  setError(null);
                }
              }}
              hoverable={isSelectable}
            >
              <Radio
                checked={plan === "custom"}
                disabled={!isSelectable}
                style={{ marginRight: 12 }}
              />
              {/* <Text strong style={{ fontSize: 24 }}>
                Custom
              </Text> */}
              <Input
                type="number"
                min={1}
                value={customValue}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "") {
                    setCustomValue("");
                    return;
                  }
                  const num = Number(val);
                  if (!isNaN(num) && num >= 1 && num <= 200) {
                    setCustomValue(val);
                  }
                }}
                style={{
                  width: 120,
                  borderRadius: 8,
                  fontSize: 16,
                  background: "transparent",
                  boxShadow: "none",
                  outline: "none",
                  border: "1px solid #d9d9d9",
                  textAlign: "center",
                }}
                prefix="$"
                placeholder="Amount"
                disabled={!isSelectable}
              />
              <Text style={{ marginLeft: 4, fontSize: 16 }}>/member</Text>
            </Card>
          </Col>
        </Row>
        {/* Show input below the cards when custom is selected */}
        {/* {plan === "custom" && (
          <Row
            justify="center"
            align="middle"
            style={{ margin: "24px 0 0 0" }}
            gutter={8}
          >
            <Col>
              <Text strong>Enter Amount*</Text>
            </Col>
            <Col>
              <Input
                type="number"
                min={1}
                value={customValue}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "") {
                    setCustomValue("");
                    return;
                  }
                  const num = Number(val);
                  if (!isNaN(num) && num >= 1 && num <= 200) {
                    setCustomValue(val);
                  }
                }}
                style={{
                  width: 120,
                  borderRadius: 8,
                  fontSize: 16,
                  background: "transparent",
                  boxShadow: "none",
                  outline: "none",
                  border: "1px solid #d9d9d9",
                  textAlign: "center",
                }}
                prefix="$"
                placeholder="Amount"
                disabled={!isSelectable}
              />
            </Col>
            <Col>
              <Text style={{ fontSize: 16 }}>/member</Text>
            </Col>
          </Row>
        )} */}
        {plan === null ? (
          <Button
            type="primary"
            size="large"
            style={{
              marginTop: 32,
              background: "#00274c",
              border: "none",
              borderRadius: 8,
              minWidth: 220,
              fontWeight: 600,
              color: "#fff",
              opacity: 0.7,
              pointerEvents: "none",
            }}
            disabled
          >
            Save Payment Plan
          </Button>
        ) : !editMode ? (
          <Button
            type="primary"
            size="large"
            style={{
              marginTop: 32,
              background: "#00274c",
              border: "none",
              borderRadius: 8,
              minWidth: 120,
              fontWeight: 600,
            }}
            onClick={handleEdit}
          >
            Edit
          </Button>
        ) : (
          <Button
            type="primary"
            size="large"
            style={{
              marginTop: 32,
              background: "#00274c",
              border: "none",
              borderRadius: 8,
              minWidth: 220,
              fontWeight: 600,
              color: "#fff",
            }}
            onClick={handleSave}
            disabled={plan === "custom" && !customValue}
          >
            Save Payment Plan
          </Button>
        )}
        {plan && (
          <>
            <div
              style={{
                fontWeight: 600,
                fontSize: 16,
                marginBottom: 12,
                textAlign: "start",
              }}
            >
              Payment history
            </div>
            <CommonTable
              columns={paymentColumns}
              dataSource={paymentHistory}
              loading={loading}
              pagination={false}
              className="common-table"
            />
          </>
        )}
      </div>
      <Modal
        // title="Add Remark"
        centered
        className="form-modal"
        footer={null}
        open={remarkModal.visible}
        width={700}
        onOk={handleRemarkSubmit}
        title={
          <Typography.Title level={4} className="modal-title">
            Mark as Paid
          </Typography.Title>
        }
        onCancel={() => setRemarkModal({ visible: false, record: null })}
        okText="Submit"
      >
        <Row gutter={20}>
          <Input.TextArea
            rows={4}
            value={remarkInput}
            maxLength={300}
            onChange={(e) => {
              const words = e.target.value.split(/\s+/).filter(Boolean);
              if (words.length <= 300) {
                setRemarkInput(e.target.value);
              }
            }}
            placeholder="Enter your remark (max 300 words)"
            style={{ marginBottom: "10px" }}
          />

          <Col span={24} md={24} xs={24} sm={24} className="center-btn">
            <Button
              size="large"
              className="theme-btn"
              htmlType="submit"
              onClick={handleRemarkSubmit}
              disabled={!remarkInput.trim()}
              loading={remarkLoading}
            >
              Mark as paid
            </Button>
          </Col>
        </Row>
      </Modal>
    </Spin>
  );
}
